/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import { AirbnbOfficialAlterationRequestStatus } from 'uplisting-frontend/models/airbnb-official-alteration-request';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type { GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import { getSummaryRouteFor } from 'uplisting-frontend/utils/rhs/routes';

interface IArgs {
  booking: ModelRegistry['booking'];
}

export interface BookingAcceptV1Signature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiBookingSummaryAcceptV1Component extends Component<BookingAcceptV1Signature> {
  @service notifications!: Services['notifications'];
  @service router!: Services['router'];

  @service('repositories/airbnb-official-alteration-request')
  airbnbOfficialAlterationRequestRepository!: Services['repositories/airbnb-official-alteration-request'];

  @tracked showCancelModal = false;
  @tracked showApproveModal = false;
  @tracked showDeclineModal = false;
  @tracked isSubmitting = false;

  @cached
  get booking(): ModelRegistry['booking'] {
    return this.args.booking;
  }

  @cached
  get changeset(): GenericChangeset<
    ModelRegistry['airbnb-official-alteration-request']
  > {
    return this.airbnbOfficialAlterationRequestRepository.buildChangeset(
      this.booking.pendingAlterationRequest,
    );
  }

  @cached
  get isMonthly(): boolean {
    return (this.router.currentRouteName ?? '').includes('monthly');
  }

  @cached
  get summaryRoute(): string {
    return getSummaryRouteFor(this.router.currentRouteName ?? '');
  }

  @cached
  get guestPriceDiff(): number {
    return (this.changeset.guestPayment || 0) - this.booking.price.grossRevenue;
  }

  @cached
  get guestPriceDiffAbs(): number {
    return Math.abs(this.guestPriceDiff);
  }

  @cached
  get isGuestDiffPositive(): boolean {
    return this.guestPriceDiff >= 0;
  }

  @cached
  get isHostDiffPositive(): boolean {
    return this.hostPriceDiff >= 0;
  }

  @cached
  get hostPriceDiff(): number {
    const newHostEarnings = this.changeset.hostPayout || 0;
    const currentHostEarnings = this.booking.price.hostEarnings || 0;

    return newHostEarnings - currentHostEarnings;
  }

  @cached
  get hostPriceDiffAbs(): number {
    return Math.abs(this.hostPriceDiff);
  }

  @action
  onReturn(): void {
    const route = this.summaryRoute;
    if (!route) {
      return;
    }

    this.router.transitionTo(route, this.booking.id);
  }

  @action
  @waitFor
  async handleCancel(): Promise<void> {
    await this.updatePendingRequest(
      AirbnbOfficialAlterationRequestStatus.cancelled,
      'action_bookings_summary_accept.change_request_cancelled',
    );
  }

  @action
  @waitFor
  async handleDecline(): Promise<void> {
    await this.updatePendingRequest(
      AirbnbOfficialAlterationRequestStatus.declined,
      'action_bookings_summary_accept.change_request_declined',
    );
  }

  @action
  @waitFor
  async handleApprove(): Promise<void> {
    await this.updatePendingRequest(
      AirbnbOfficialAlterationRequestStatus.accepted,
      'action_bookings_summary_accept.change_request_approved',
    );
  }

  private async updatePendingRequest(
    status: AirbnbOfficialAlterationRequestStatus,
    message: string,
  ): Promise<void> {
    this.changeset.set('status', status);

    this.isSubmitting = true;

    try {
      await this.changeset.save();

      await this.booking.reload();
      this.notifications.info(message);
      this.onReturn();
    } catch {
      this.notifications.error();
    } finally {
      this.isSubmitting = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Summary::Accept::V1': typeof UiBookingSummaryAcceptV1Component;
  }
}
